import React, { useState, useEffect } from 'react'
import { Edit, Trash2, Download, Check, X, Copy, RefreshCw } from 'lucide-react'
import { Shortcode, UpdateShortcodeRequest, ShortcodeAnalytics } from '../types'
import { updateShortcode, deleteShortcode, generateQRCode, getShortcodeStats } from '../api'

interface ShortcodeDetailProps {
  shortcode: Shortcode
  onUpdate: (shortcode: Shortcode) => void
  onDelete: (shortcode: string) => void
}

const ShortcodeDetail: React.FC<ShortcodeDetailProps> = ({
  shortcode,
  onUpdate,
  onDelete
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState<UpdateShortcodeRequest>({
    url: shortcode.url,
    description: shortcode.description,
    androidUrl: shortcode.androidUrl || '',
    iosUrl: shortcode.iosUrl || '',
    linuxUrl: shortcode.linuxUrl || '',
    macUrl: shortcode.macUrl || '',
    windowsUrl: shortcode.windowsUrl || ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>('')
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null)
  const [isQrLoading, setIsQrLoading] = useState(false)
  const [isDownloadingQr, setIsDownloadingQr] = useState(false)
  const [qrError, setQrError] = useState<string>('')
  const [analytics, setAnalytics] = useState<ShortcodeAnalytics | null>(null)
  const [analyticsLoading, setAnalyticsLoading] = useState(false)
  const [analyticsError, setAnalyticsError] = useState<string>('')

  const normalizeAnalytics = (data: ShortcodeAnalytics): ShortcodeAnalytics => {
    return {
      ...data,
      platformData: data.platformData || {},
      viewHistory: Array.isArray(data.viewHistory) ? data.viewHistory : []
    }
  }

  const handleRefreshQr = async () => {
    setIsQrLoading(true)
    setQrError('')
    try {
      const qrCode = await generateQRCode(shortcode.shortcode)
      if (qrCode.success && qrCode.data) {
        setQrCodeUrl(qrCode.data)
      } else {
        setQrError(qrCode.error || 'Failed to generate QR code')
        setQrCodeUrl(null)
      }
    } catch (err) {
      setQrError('Failed to generate QR code')
      setQrCodeUrl(null)
    } finally {
      setIsQrLoading(false)
    }
  }

  // Sync form state when a different shortcode is selected
  useEffect(() => {
    setFormData({
      url: shortcode.url,
      description: shortcode.description,
      androidUrl: shortcode.androidUrl || '',
      iosUrl: shortcode.iosUrl || '',
      linuxUrl: shortcode.linuxUrl || '',
      macUrl: shortcode.macUrl || '',
      windowsUrl: shortcode.windowsUrl || ''
    })
    setIsEditing(false)
  }, [shortcode])

  // Load QR preview and analytics whenever shortcode changes
  useEffect(() => {
    let isCurrent = true

    const loadQr = async () => {
      setIsQrLoading(true)
      setQrError('')
      try {
        const qrCode = await generateQRCode(shortcode.shortcode)
        if (!isCurrent) return
        if (qrCode.success && qrCode.data) {
          setQrCodeUrl(qrCode.data)
        } else {
          setQrError(qrCode.error || 'Failed to generate QR code')
          setQrCodeUrl(null)
        }
      } catch (err) {
        if (isCurrent) {
          setQrError('Failed to generate QR code')
          setQrCodeUrl(null)
        }
      } finally {
        if (isCurrent) {
          setIsQrLoading(false)
        }
      }
    }

    const loadAnalytics = async () => {
      setAnalyticsLoading(true)
      setAnalyticsError('')
      try {
        const response = await getShortcodeStats(shortcode.shortcode)
        if (!isCurrent) return
        if (response.success && response.data) {
          setAnalytics(normalizeAnalytics(response.data))
        } else {
          setAnalytics(null)
          setAnalyticsError(response.error || 'Failed to load analytics')
        }
      } catch (err) {
        if (isCurrent) {
          setAnalytics(null)
          setAnalyticsError('Failed to load analytics')
        }
      } finally {
        if (isCurrent) {
          setAnalyticsLoading(false)
        }
      }
    }

    loadQr()
    loadAnalytics()

    return () => {
      isCurrent = false
    }
  }, [shortcode.shortcode])

  const handleSave = async () => {
    setLoading(true)
    setError('')

    try {
      const result = await updateShortcode(shortcode.shortcode, formData)
      if (result.success && result.data) {
        onUpdate({
          ...shortcode,
          ...formData
        })
        setIsEditing(false)
      } else {
        setError(result.error || 'Failed to update shortcode')
      }
    } catch (err) {
      setError('An error occurred while updating the shortcode')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this shortcode?')) {
      setLoading(true)
      setError('')

      try {
        const result = await deleteShortcode(shortcode.shortcode)
        if (result.success) {
          onDelete(shortcode.shortcode)
        } else {
          setError(result.error || 'Failed to delete shortcode')
        }
      } catch (err) {
        setError('An error occurred while deleting the shortcode')
      } finally {
        setLoading(false)
      }
    }
  }

  const handleDownloadQR = async () => {
    setIsDownloadingQr(true)
    setQrError('')
    try {
      let dataUrl = qrCodeUrl
      if (!dataUrl) {
        const qrCode = await generateQRCode(shortcode.shortcode)
        if (qrCode.success && qrCode.data) {
          dataUrl = qrCode.data
          setQrCodeUrl(qrCode.data)
        } else {
          setQrError(qrCode.error || 'Failed to generate QR code')
          return
        }
      }

      if (dataUrl) {
        // Convert data URL to blob for proper PNG download
        const response = await fetch(dataUrl)
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `qr-${shortcode.shortcode}.png`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }
    } catch (err) {
      setQrError('Failed to generate QR code')
    } finally {
      setIsDownloadingQr(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const fullShortUrl = `${import.meta.env.VITE_SHORT_URL_BASE || window.location.origin}/s/${shortcode.shortcode}`

  const formatDateTime = (value?: string) => {
    if (!value) return '—'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) {
      return value
    }
    return date.toLocaleString()
  }

  return (
    <div className="card hover-lift scale-in">
      <div className="card-header">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="flex items-center space-x-3">
              <span className="font-mono text-lg bg-blue-100 text-blue-800 px-3 py-1 rounded-lg font-semibold">
                {shortcode.shortcode}
              </span>
              {isEditing && (
                <span className="text-sm bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full font-medium">
                  Editing
                </span>
              )}
            </div>
            <p className="text-caption text-gray-600">
              Created: {new Date(shortcode.createdAt).toLocaleDateString()}
            </p>
          </div>
          <div className="flex space-x-2">
            {!isEditing ? (
              <>
                <button
                  onClick={() => setIsEditing(true)}
                  className="btn-secondary flex items-center space-x-2"
                >
                  <Edit className="h-4 w-4" />
                  <span>Edit</span>
                </button>
                <button
                  onClick={handleDownloadQR}
                  disabled={isDownloadingQr}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
                >
                  {isDownloadingQr ? (
                    <div className="loading-spinner h-4 w-4" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  <span>{isDownloadingQr ? 'Downloading...' : 'QR Code'}</span>
                </button>
                <button
                  onClick={handleDelete}
                  className="btn-danger flex items-center space-x-2"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>Delete</span>
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setIsEditing(false)}
                  className="btn-secondary flex items-center space-x-2"
                >
                  <X className="h-4 w-4" />
                  <span>Cancel</span>
                </button>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="btn-primary flex items-center space-x-2 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <div className="loading-spinner h-4 w-4"></div>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4" />
                      <span>Save Changes</span>
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="card-body space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 fade-in">
            <p className="text-red-800 text-sm font-medium">{error}</p>
          </div>
        )}

        {/* Enhanced Short URL Display */}
        <div className="space-y-2">
          <label className="form-label">
            Short URL
          </label>
          <div className="flex items-center space-x-3">
            <input
              type="text"
              value={fullShortUrl}
              readOnly
              className="form-input bg-gray-50 flex-1"
            />
            <button
              onClick={() => navigator.clipboard.writeText(fullShortUrl)}
              className="btn-secondary flex items-center space-x-2"
            >
              <Copy className="h-4 w-4" />
              <span>Copy</span>
            </button>
          </div>
        </div>

        {/* Main URL */}
        <div className="space-y-2">
          <label htmlFor="url" className="form-label">
            Destination URL
          </label>
          <input
            type="url"
            id="url"
            name="url"
            value={formData.url}
            onChange={handleChange}
            readOnly={!isEditing}
            className="form-input disabled:bg-gray-50"
          />
        </div>

        {/* Description */}
        <div className="space-y-2">
          <label htmlFor="description" className="form-label">
            Description
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            readOnly={!isEditing}
            rows={3}
            className="form-input resize-none disabled:bg-gray-50"
          />
        </div>

        {/* Platform-Specific URLs */}
        <div className="border-t border-gray-100 pt-6">
          <h3 className="text-subheading text-gray-900 mb-4">Platform-Specific URLs</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {['android', 'ios', 'linux', 'mac', 'windows'].map((platform) => (
              <div key={platform} className="space-y-2">
                <label htmlFor={`${platform}Url`} className="form-label">
                  {platform.charAt(0).toUpperCase() + platform.slice(1)} URL
                </label>
                <input
                  type="url"
                  id={`${platform}Url`}
                  name={`${platform}Url`}
                  value={formData[`${platform}Url` as keyof UpdateShortcodeRequest] as string}
                  onChange={handleChange}
                  readOnly={!isEditing}
                  className="form-input disabled:bg-gray-50"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-gray-100 pt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* QR Preview */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-subheading text-gray-900">QR Code Preview</h3>
              <button
                onClick={handleRefreshQr}
                className="btn-secondary flex items-center space-x-2"
              >
                <RefreshCw className="h-4 w-4" />
                <span>Refresh</span>
              </button>
            </div>
            <div className="border border-dashed border-gray-300 rounded-xl p-6 flex items-center justify-center bg-gray-50 min-h-[220px]">
              {isQrLoading ? (
                <div className="text-center space-y-3">
                  <div className="loading-spinner mx-auto"></div>
                  <p className="text-caption text-gray-600">Generating QR code...</p>
                </div>
              ) : qrError ? (
                <p className="text-caption text-red-600 text-center">{qrError}</p>
              ) : qrCodeUrl ? (
                <img
                  src={qrCodeUrl}
                  alt={`QR code for ${shortcode.shortcode}`}
                  className="max-h-48 w-auto drop-shadow"
                />
              ) : (
                <p className="text-caption text-gray-500 text-center">
                  QR code preview unavailable
                </p>
              )}
            </div>
          </div>

          {/* Analytics */}
          <div className="space-y-4">
            <h3 className="text-subheading text-gray-900">Analytics</h3>
            {analyticsLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="loading-spinner"></div>
              </div>
            ) : analyticsError ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-700">{analyticsError}</p>
              </div>
            ) : analytics ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                    <p className="text-caption text-blue-700">Total Views</p>
                    <p className="text-2xl font-semibold text-blue-900">
                      {analytics.totalViews}
                    </p>
                  </div>
                  <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4">
                    <p className="text-caption text-indigo-700">Last Access</p>
                    <p className="text-body text-indigo-900">
                      {analytics.lastAccess ? formatDateTime(analytics.lastAccess) : 'No views yet'}
                    </p>
                  </div>
                </div>

                <div>
                  <h4 className="text-caption uppercase tracking-wide text-gray-500 mb-2">Views by Platform</h4>
                  {Object.keys(analytics.platformData || {}).length === 0 ? (
                    <p className="text-caption text-gray-500">No platform data yet.</p>
                  ) : (
                    <ul className="space-y-2">
                      {Object.entries(analytics.platformData)
                        .sort((a, b) => b[1] - a[1])
                        .map(([platform, count]) => (
                          <li key={platform} className="flex justify-between text-body text-gray-700">
                            <span className="capitalize">{platform || 'unknown'}</span>
                            <span className="font-medium">{count}</span>
                          </li>
                        ))}
                    </ul>
                  )}
                </div>

                <div>
                  <h4 className="text-caption uppercase tracking-wide text-gray-500 mb-2">Recent Activity</h4>
                  {analytics.viewHistory.length === 0 ? (
                    <p className="text-caption text-gray-500">No recent views.</p>
                  ) : (
                    <div className="bg-white border border-gray-100 rounded-lg divide-y divide-gray-100">
                      {analytics.viewHistory.slice(0, 5).map((view, index) => (
                        <div key={`${view.timestamp}-${index}`} className="px-4 py-3 flex justify-between text-sm text-gray-700">
                          <div>
                            <p className="font-medium">{formatDateTime(view.timestamp)}</p>
                            <p className="text-caption text-gray-500">IP: {view.ipAddress || 'N/A'}</p>
                          </div>
                          <span className="capitalize bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs h-fit">
                            {view.platform || 'unknown'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  {analytics.viewHistory.length > 5 && (
                    <p className="text-caption text-gray-500 mt-2">
                      Showing 5 of {analytics.viewHistory.length} records
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-caption text-gray-500">No analytics data available.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ShortcodeDetail